import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../translations/translationsConstants";
import SectionHeader from "./SectionHeader";
import { useContext, useEffect, useRef, useState } from "react";
import { previewHtml, previewPdf } from "../../api";
import { MetaforceContext, WebEditorContext } from "../../contexts";
import * as Constants from "../../utils/constants";
import { useErrorHandler } from "../../hooks/useErrorHandler";

export default function PreviewView() {
    const { startParameter, toastHandler, customErrorHandler } = useContext(MetaforceContext);
    const { previewType, previewRefreshDisabled, setPreviewRefreshDisabled, forcePreviewLoad, actionMenuBar } = useContext(WebEditorContext);
    const errorHandler = useErrorHandler();
    const { t: terr } = useTranslation([resources.errors]);
    const { t: tc } = useTranslation([resources.common]);
    const [previewTitle, setPreviewTitle] = useState(tc(translations.common.pdfPreview));
    const [blobUrl, setBlobUrl] = useState();
    const isInitialLoad = useRef(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadHtml = async () => {
        const res = await previewHtml(startParameter);
        const blob = new Blob([res], { type: "text/html" });
        const newBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(newBlobUrl);

        setPreviewTitle(tc(translations.common.htmlPreview));
    }

    const loadPdf = async () => {
        const res = await previewPdf(startParameter);
        const blob = new Blob([res], { type: "application/pdf" });
        const newBlobUrl = URL.createObjectURL(blob);
        setBlobUrl(newBlobUrl ? newBlobUrl + "#toolbar=1&navpanes=0&view=FitH" : undefined);

        setPreviewTitle(tc(translations.common.pdfPreview));
    }

    const loadData = async () => {
        setLoading(true);
        setError(false);
        try {
            switch (previewType) {
                case Constants.previewType.html:
                    await loadHtml();
                    break;
                default:
                    await loadPdf();
                    break;
            }
        } catch (error) {
            setError(true);
            errorHandler.handleApiError(error, terr(translations.errors.failLoadFile), false, null, customErrorHandler, toastHandler);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (isInitialLoad.current) return;
        isInitialLoad.current = true;
        loadData();
    }, [])

    useEffect(() => {
        if (forcePreviewLoad) {
            loadData();
        }
    }, [forcePreviewLoad])

    const handleRefresh = () => {
        setPreviewRefreshDisabled(true);
        loadData();
    }

    return (
        <div className="tw-flex tw-flex-col tw-h-full">
            <SectionHeader>
                <div className="tw-flex tw-justify-between">
                    <span className="tw-text-sm tw-font-medium">{previewTitle}</span>
                    <button
                        type="button"
                        aria-label={previewRefreshDisabled ? tc(translations.common.refreshDisabled) : tc(translations.common.refreshWithLatest)}
                        className="button-tooltip-disabled-container tw-px-1 tw-py-1 tw-bg-white tw-flex tw-items-center tw-text-gray-500 tw-border tw-border-gray-300 tw-rounded-md
                            hover:tw-text-gray-600 hover:tw-bg-gray-50 
                            focus:tw-outline-none focus:tw-border-indigo-600 focus:tw-ring-indigo-600 focus:tw-ring-2 
                            disabled:tw-bg-gray-200 disabled:tw-text-gray-400 disabled:tw-cursor-not-allowed"
                        disabled={previewRefreshDisabled}
                        onClick={handleRefresh}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleRefresh();
                            }
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="tw-h-4 tw-w-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        <span
                            role="tooltip"
                            id="preview-tooltip-desc"
                            className="button-tooltip disabled:tw-block">
                            {previewRefreshDisabled ? tc(translations.common.refreshDisabled) : tc(translations.common.refreshWithLatest)}
                        </span>
                    </button>
                </div>
            </SectionHeader>

            <div className="tw-h-full tw-w-full tw-border tw-shadow-2xl tw-rounded-sm tw-scroll-smooth tw-bg-white">
                {error && (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                        <div className="tw-text-center">
                            <p className="tw-text-gray-600 tw-mb-4">
                                {terr(translations.errors.failLoadFile)}
                            </p>
                        </div>
                    </div>
                )}
                {(!error && (loading || !blobUrl)) && (
                    <div className="tw-flex tw-items-center tw-justify-center tw-h-full">
                        <div className="tw-text-center">
                            <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600 tw-mx-auto tw-mb-4"></div>
                            <p className="tw-text-gray-600">{tc(translations.common.loading)} {previewTitle}...</p>
                        </div>
                    </div>
                )}
                {!error && !loading && blobUrl && (
                    <iframe
                        width='100%'
                        height='100%'
                        src={blobUrl}
                    />
                )}
            </div>
        </div>
    )
}